import type { RouteLocationNormalized, RouterScrollBehavior } from 'vue-router';

/**
 * App-wide scroll policy for vue-router.
 *
 * - Back / forward: restore the saved position.
 * - A query-only update on the same page and the same `?tab=` (a drawer
 *   opening via `?item=`, a view switch, a filter, a toggle mirrored to the
 *   URL with `router.replace`): keep the current position — jumping to the
 *   top there feels like the page reloaded.
 * - Anything else (another page, another `:id`, another tab, a hash):
 *   scroll to the top, as before.
 */
export const appScrollBehavior: RouterScrollBehavior = (to, from, savedPosition) => {
  if (savedPosition) return savedPosition;
  if (isSamePageQueryUpdate(to, from)) return false;
  return { top: 0 };
};

/** Query keys that change which slice of a list is shown; moving between pages starts at the top. */
const PAGINATION_KEYS = ['page', 'perPage'] as const;

/** Same path (so same route and params), same hash, same `tab` and same page; only other query keys differ. */
export function isSamePageQueryUpdate(to: RouteLocationNormalized, from: RouteLocationNormalized): boolean {
  // The initial navigation comes from START_LOCATION, which matches nothing.
  if (from.matched.length === 0) return false;
  if (to.path !== from.path || to.hash !== from.hash) return false;
  if (firstValue(to.query.tab) !== firstValue(from.query.tab)) return false;
  return PAGINATION_KEYS.every((key) => firstValue(to.query[key]) === firstValue(from.query[key]));
}

function firstValue(value: unknown): string | null {
  const v = Array.isArray(value) ? value[0] : value;
  return typeof v === 'string' ? v : null;
}
